'use client'

import { ExternalLink, QrCode, Smartphone } from 'lucide-react'
import { Button } from '@/components/platform/v2/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/platform/v2/ui/dialog'
import type { CaseStudy } from '@/data/cases'
import Link from 'next/link'

interface Props {
    item: CaseStudy | null
    open: boolean
    onOpenChange: (open: boolean) => void
}

const CaseLinksDialog = ({ item, open, onOpenChange }: Props) => {
    if (!item) return null

    const hasLinks = Boolean(item.appUrl || item.websiteUrl)

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto border-border/70 bg-[hsl(var(--charcoal))] p-6 sm:p-8">
                <DialogHeader className="pr-7 text-left">
                    <div className="mb-3 flex items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-sm font-extrabold text-primary-foreground">
                            {item.marker}
                        </span>
                        <span className="pill pill-soon">Тариф {item.plan}</span>
                    </div>
                    <DialogTitle className="display-3">{item.client}</DialogTitle>
                    <DialogDescription className="body-text">{item.shortDescription}</DialogDescription>
                </DialogHeader>

                {item.qrImage && (
                    <div className="mx-auto mt-2 w-full max-w-[220px] rounded-2xl bg-foreground p-4">
                        <img
                            src={item.qrImage}
                            alt={`QR-код приложения ${item.client}`}
                            className="aspect-square w-full object-contain"
                            loading="lazy"
                        />
                    </div>
                )}

                <div className="mt-2 grid gap-3">
                    {item.appUrl && (
                        <Button asChild className="h-auto min-h-12 justify-between rounded-full px-5 py-3 font-bold">
                            <a href={item.appUrl} target="_blank" rel="noreferrer">
                                <span className="flex items-center gap-2">
                                    <Smartphone /> Открыть приложение
                                </span>
                                <ExternalLink />
                            </a>
                        </Button>
                    )}
                    {item.websiteUrl && (
                        <Button asChild variant="outline" className="h-auto min-h-12 justify-between rounded-full px-5 py-3">
                            <a href={item.websiteUrl} target="_blank" rel="noreferrer">
                                <span className="flex items-center gap-2">
                                    <ExternalLink /> Перейти на сайт
                                </span>
                                <ExternalLink />
                            </a>
                        </Button>
                    )}
                    {!hasLinks && (
                        <div className="surface-quiet flex items-center gap-3 text-left">
                            <QrCode className="h-5 w-5 shrink-0 text-primary" />
                            <p className="body-text">Ссылки на приложение и сайт скоро появятся здесь.</p>
                        </div>
                    )}
                </div>
                <Button asChild className="mt-3 w-full rounded-full">
                    <Link href={`/signin?source=case&case=${item.slug}`}>Хочу похожее решение</Link>
                </Button>
            </DialogContent>
        </Dialog>
    )
}

export default CaseLinksDialog
